import React, { useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import './style.scss';
import ButtonWithProgress from '../../../_shared/ui-components/YogaUI/ButtonWithProgress';
import { ALO_API_HOST } from '../../../../utils/constants';
import FormInput from '../../../_shared/ui-components/YogaUI/FormInput';
import { formatDateWithLocaleOptions } from '../../../../utils/helpers';
import { prependLocaleToPath } from '../../../../utils/localization';

type Money = {
  amount: number;
  currency_code: string;
};

type GiftCardBalanceResponse = {
  balance: Money;
  initial_value: Money;
  expires_on: string | null;
};

export const formatBalance = (input: number, currency: string) =>
  input.toLocaleString(undefined, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const GiftCardBalancePage = () => {
  const [giftCardCode, setGiftCardCode] = useState<string>('');
  const [balanceResponse, setBalanceResponse] = useState<GiftCardBalanceResponse | null>(null);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setLoading(true);
      setError(null);
      const response = await fetch(`${ALO_API_HOST}/v1/account/gift_cards/balance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: giftCardCode.replace(/[-\s]+/g, ''),
        }),
      });
      setLoading(false);
      if (response.ok) {
        const data: GiftCardBalanceResponse = await response.json();
        setBalanceResponse(data);
      } else if (response.status === 404) {
        setError('Gift card not found');
      } else {
        setError('Error looking up gift card balance. Please try again.');
      }
    },
    [giftCardCode]
  );

  const resetState = () => {
    setGiftCardCode('');
    setBalanceResponse(null);
    setError(null);
    setLoading(false);
  };

  const formattedExpirationDate = useMemo(() => {
    return balanceResponse?.expires_on ? formatDateWithLocaleOptions(balanceResponse.expires_on) : null;
  }, [balanceResponse]);

  return (
    <div className="container d-flex justify-content-center">
      <div className="gift-card-balance-container d-flex align-items-center flex-column text-center">
        <h1 className="h2 uppercase">Check gift card balance</h1>
        {balanceResponse ? (
          <>
            <div className="body-large">
              <p>The current balance on gift card {giftCardCode} is:</p>
            </div>
            <div className="gift-card-balance-amount">
              <h2 className="h2">
                {formatBalance(balanceResponse.balance.amount, balanceResponse.balance.currency_code)}
              </h2>
            </div>
            <div className="gift-card-balance-description flex-column">
              <p>
                Initial value:{' '}
                {formatBalance(balanceResponse.initial_value.amount, balanceResponse.initial_value.currency_code)}
              </p>
              {balanceResponse.expires_on && <p>Expires on: {formattedExpirationDate}</p>}
            </div>
            <div className="gift-card-balance-buttons-container d-flex flex-column align-items-center">
              <button
                className="button-primary gift-card-balance-button"
                onClick={() => (window.location.href = prependLocaleToPath('/collections/new-arrivals'))}
              >
                Shop now
              </button>
              <button className="button-ghost gift-card-balance-button" onClick={resetState}>
                Check another gift card
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="body-large">
              Enter your e-gift card or physical gift card code to check your current balance and expiration date.
            </p>
            <form className="gift-card-balance-form" onSubmit={handleSubmit}>
              <FormInput
                options={{
                  id: 'giftCardCode',
                  label: 'Your gift card code',
                  type: 'text',
                  required: true,
                  errorText: 'Please enter a valid gift card code',
                  pattern: '^(?:[a-zA-Z0-9]([\\s\\-]?)){16}$',
                  onChange: (event) => {
                    setGiftCardCode(event.currentTarget.value);
                  },
                  handleFormState: setIsValid,
                }}
              />
              {error && <p className="body red">{error}</p>}
              <div className="gift-card-balance-section gift-card-balance-button-section">
                <ButtonWithProgress
                  options={{
                    showProgressAnimation: loading,
                    cta: 'Check balance',
                    type: 'submit',
                    disabled: !isValid,
                    buttonClasses: 'gift-card-balance-button',
                  }}
                />
              </div>
            </form>
          </>
        )}
        <p className="body-large">
          Questions? Visit our{' '}
          <a className="body-text-link" href={prependLocaleToPath('/pages/payments-support')}>
            Help Center
          </a>
        </p>
      </div>
    </div>
  );
};

export default GiftCardBalancePage;

const mountNode = document.querySelector('[react-render-target="gift-card-balance-page"]');
mountNode
  ? ReactDOM.render(<GiftCardBalancePage />, mountNode)
  : console.warn('missing render target: GiftCardBalancePage');
